import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import '../Css/Interview_questions.css';
import Primary_Button from '../Components/Primary_Button';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { setAccessToken, setCurrentQuestionId, setFollowupQuestions, setQuestionCount, setQuestions } from '../Redux/UserSlice';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useNavigate } from 'react-router';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import CodeAnswerEditor from '../Components/CodeAnswerEditor';
import InterviewQuestion from '../Components/Interview_Question';
import { Switch, FormControlLabel } from "@mui/material";
import { styled } from '@mui/material/styles';
import posthog from 'posthog-js';
import Loader from '../Components/Loader';

function Interview_Questions() {
    const timerRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        interviewId,
        currentQuestionId,
        accessToken,
        // followupQuestions,
        questionCount,
        questions,
        interviewKey
    } = useSelector((state) => state.User, shallowEqual);

    const [followupQuestions, setFollowupQuestions] = useState([]);
    const [isCodeMode, setIsCodeMode] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState(true);
    const [questionData, setQuestionData] = useState();
    const [questionBody, setQuestionBody] = useState("");
    const [answer, setAnswer] = useState("");
    const [codeLanguage, setCodeLanguage] = useState("");
    const [isFollowup, setIsFollowup] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [totalTime, setTotalTime] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const setData = useCallback((id, timeLimit, question, language) => {
        dispatch(setQuestionCount(id));
        dispatch(setCurrentQuestionId(id));

        const remaining = getRemainingTime(id, timeLimit);

        setTimeLeft(remaining);
        setTotalTime(timeLimit);

        if (!localStorage.getItem(`timer_${id}`)) {
            localStorage.setItem(`timer_${id}`, JSON.stringify({ startTime: Date.now(), timeLimit }));
        }

        setQuestionBody(question);
        setCodeLanguage(language);
        setAnswer("");
    }, [dispatch]);

    const fetchQuestion = useCallback(async (updatedQuestions) => {
        // console.log("questions array before fetching question is : ", questions);
        // console.log("updatedQuestions array before fetching question is : ", updatedQuestions);

        setLoadingText("Getting your question ready...");

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/v2/next_question/${interviewId}`);

            if (!res.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }

            const data = await res.json();
            console.log("(Fetch Question) Response:", data);

            if (data.status === "completed") {
                completeInterview();
            }
            else if (data.status === "success") {
                const q = data?.question;
                if (q?.is_followup) {
                    const followupChain = q.followup_chain;
                    const questionsToUse = updatedQuestions ?? questions;
                    const followup_questions = questionsToUse.filter((qItem) =>
                        followupChain.includes(qItem.id)
                    );
                    setFollowupQuestions(followup_questions)
                }

                setQuestionData(q);
                setIsFollowup(q.is_followup);
                dispatch(setAccessToken(q.access_token));
                setData(q.id, q.time_limit, q.question, q.code_language);
                setLoading(false);
            } else {
                setLoading(false);
                toast.error(data?.detail);
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to fetch question");
            setLoading(false);
        }
    }, [interviewId, dispatch, setData]);

    const skipQuestion = useCallback(async () => {
        if (isSubmitting) return;

        if (timerRef?.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        setIsSubmitting(true);
        setLoading(true);
        setLoadingText("Skipping to the next question...");

        // console.log("Skipping Question: ", questionData);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/v2/skip_question/${interviewId}/${currentQuestionId}`, {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ access_token: accessToken })
            });

            if (!res?.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }

            const json = await res?.json();

            if (json?.status === "success") {
                let questionObj = {
                    id: questionData?.id || currentQuestionId,
                    access_token: questionData?.access_token || accessToken,
                    question: questionData?.question || questionBody,
                    answer: answer
                }
                const updatedQuestions = [...questions, questionObj];
                fetchQuestion(updatedQuestions);
            } else {
                setLoading(false);
                toast.error(json?.detail);
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
            toast.error("Failed to skip question");
        } finally {
            setIsSubmitting(false);
        }
    }, [interviewId, currentQuestionId, accessToken, fetchQuestion]);

    const submitAnswer = useCallback(async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);

        if (timerRef?.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        setLoading(true);
        setLoadingText("Submitting your response, this might take up to a minute...");
        console.log("Access Token of question being submitted:", accessToken);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/v2/submit_answer/${interviewId}/${currentQuestionId}`, {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ answer, access_token: accessToken })
            });

            // if (!res.ok) {
            //     throw new Error(`HTTP error! Status: ${res.status}`);
            // }

            const json = await res?.json();
            console.log("Response JSON:", json);

            setIsSubmitting(false);

            const questionObj = {
                id: questionData?.id || currentQuestionId,
                access_token: questionData?.access_token || accessToken,
                question: questionData?.question || questionBody,
                answer: answer
            };

            const updatedQuestions = [...questions, questionObj];
            if (json?.status === "success") {
                dispatch(setQuestions(questionObj));
                localStorage.removeItem(`timer_${currentQuestionId}`);
            } else {
                toast.error(json?.detail || "Unknown error occurred");
                setLoading(false);
            }
            fetchQuestion(updatedQuestions);
        } catch (error) {
            console.error("Failed to submit answer:", error);
            console.error("Failed to submit answer 2:", error?.message);
            toast.error("Failed to submit answer. Please try again.");
            setLoading(false);
            setIsSubmitting(false);
        }
    }, [interviewId, currentQuestionId, answer, accessToken, questionData, questions, dispatch, fetchQuestion]);

    const completeInterview = useCallback(async () => {
        setLoadingText("Your interview is complete! Evaluating your final result. This may take up to 2 minutes...");

        try {
            console.log("(Complete Interview Function): Interview ID -", interviewId);

            const res = await fetch(`${process.env.REACT_APP_API_URL}/v2/complete_interview/${interviewId}`, {
                method: "POST",
                headers: { 'Content-Type': 'application/json' }
            });

            if (!res?.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }

            const json = await res?.json();
            console.log("Complete interview response:", json);

            if (json?.status === "success") {
                posthog?.sessionRecording?.stopRecording();
                posthog?.opt_out_capturing();
                navigate('/interview-score');
            } else {
                toast.error(json?.detail || "An unexpected error occurred.");
            }

        } catch (error) {
            console.error("Error completing interview:", error);
            toast.error("Failed to complete interview. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [interviewId, navigate]);


    const getRemainingTime = (questionId, timeLimit) => {
        const stored = localStorage?.getItem(`timer_${questionId}`);
        if (stored) {
            const { startTime } = JSON.parse(stored);
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            return Math.max(0, timeLimit - elapsed);
        }
        return timeLimit;
    };


    useEffect(() => {
        fetchQuestion();
        console.log("Interview key: ", interviewKey);

        // console.log(followupQuestions);
    }, [fetchQuestion]);

    useEffect(() => {
        const stored = localStorage.getItem(`timer_${currentQuestionId}`);
        if (!stored) return;

        const { startTime, timeLimit } = JSON.parse(stored);

        const interval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = Math.max(0, timeLimit - elapsed);
            setTimeLeft(remaining);
        }, 1000);

        timerRef.current = interval;
        return () => clearInterval(interval);
    }, [currentQuestionId]);


    useEffect(() => {
        if (timeLeft === 0 && totalTime > 0) submitAnswer();
    }, [timeLeft, totalTime]);

    const formatTime = useCallback((seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }, []);

    const progress = useMemo(() => {
        if (!totalTime || totalTime === 0) return 0;
        const rawProgress = (timeLeft / totalTime) * 100;
        return Math.max(0, Math.min(rawProgress, 100)); // Clamp between 0 and 100
    }, [timeLeft, totalTime]);

    const formattedTime = useMemo(() => {
        if (!Number.isFinite(timeLeft)) return "00:00";
        return formatTime(Math.max(0, Math.floor(timeLeft)));
    }, [timeLeft, formatTime]);

    const AntSwitch = styled(Switch)(({ theme }) => ({
        width: 60,
        height: 30,
        padding: 0,
        display: 'flex',
        '&:active .MuiSwitch-thumb': {
            width: 28,
        },
        '& .MuiSwitch-switchBase': {
            padding: 2,
            transition: 'all 0.3s ease-in-out',
            '&.Mui-checked': {
                transform: 'translateX(30px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    background: '#F59407',
                    opacity: 1,
                    boxShadow: '0 0 6px rgba(245, 148, 7, 0.5)',
                },
                '& .MuiSwitch-thumb': {
                    backgroundColor: '#fff',
                    color: '#F59407',
                    '&::before': {
                        content: '"</>"',
                    },
                },
            },
        },
        '& .MuiSwitch-thumb': {
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
            backgroundColor: '#F59407',
            width: 26,
            height: 25,
            borderRadius: '50%',
            transition: 'all 0.3s ease',
            color: '#fff',
            fontWeight: 600,
            fontSize: 12,
            fontFamily: 'monospace',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
                content: '"Tt"',
                display: 'block',
            },
        },
        '& .MuiSwitch-track': {
            borderRadius: 30,
            opacity: 1,
            backgroundColor: '#ffffff',
            boxShadow: 'inset 0 0 5px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease-in-out',
        },
    }));



    return (
        <>
            <Loader loading={loading} message={loadingText} />
            {/* {loading && (
                <div className="overlay">
                    <RiseLoader loading={loading} color='#F49507' size={24} cssOverride={{ display: "block", margin: "0 auto", zIndex: 5 }} />
                    <label>{loadingText}</label>
                </div>
            )} */}
            <ToastContainer position="top-right" autoClose={6000} pauseOnHover={false} theme="light" transition={Bounce} />
            <div className="interview_link_body_container">
                <div className="page_header"><p>INTERVIEW QUESTION</p></div>
                <div className="interview_questionspage_section interview_questionspage_section1">
                    <h3>Question {questionCount}</h3>
                    <div style={{ width: 60, height: 60 }}>
                        <CircularProgressbar
                            value={progress}
                            text={formattedTime}
                            styles={buildStyles({
                                pathColor: timeLeft < 10 ? "#FF0000" : "#F49507",
                                textColor: "#000",
                                trailColor: "#d6d6d6",
                                textSize: "24px"
                            })}
                        />
                    </div>
                </div>
                <div className="interview_questionspage_section interview_questionspage_section2">
                    <div className="interview_questionspage_body">
                        <InterviewQuestion
                            questionNumber={questionCount}
                            questionBody={questionBody}
                            followupQuestions={followupQuestions}
                            isFollowup={isFollowup}
                        />
                        <div className="interview_answer_container">
                            <div className="interview_questionspage_body_heading" style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "12px"
                            }}>
                                <p style={{
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    margin: 0
                                }}>Answer:</p>

                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <AntSwitch
                                        checked={isCodeMode}
                                        onChange={(e) => setIsCodeMode(e.target.checked)}
                                    />

                                    <div style={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        color: "#F59407",
                                        background: "rgba(245,148,7,0.1)",
                                        // color: isCodeMode ? "#F59407" : "#333",
                                        // background: isCodeMode ? "rgba(245,148,7,0.1)" : "rgba(0,0,0,0.05)",
                                        padding: "6px 12px",
                                        borderRadius: "8px",
                                        transition: "all 0.3s ease",
                                        boxShadow: "inset 0 0 2px rgba(0,0,0,0.1)"
                                    }}>
                                        {isCodeMode ? "Code Editor" : "Text Editor"}
                                    </div>
                                </div>
                            </div>

                            <div className="interview_question_content">
                                {isCodeMode ? (

                                    <CodeAnswerEditor
                                        value={answer}
                                        onChange={setAnswer}
                                        language={codeLanguage?.toLowerCase()}
                                        height="580px"
                                    />
                                ) : (
                                    <textarea
                                        value={answer}
                                        onChange={(e) => setAnswer(e.target.value)}
                                        placeholder="Type your answer here..."
                                        style={{
                                            width: "100%",
                                            height: "580px",
                                            fontSize: "14px",
                                            fontFamily: "sans-serif",
                                            padding: "0px 20px",
                                            borderRadius: "6px",
                                            border: "none",
                                            resize: "none",
                                            // backgroundColor: "#f9f9f9",
                                            outline: "none",
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="interview_question_button_container">
                        <div onClick={skipQuestion}>
                            <Primary_Button
                                bgColorProp="#f5f5f5"
                                colorProp="black"
                                fontWeightProp="600"
                                paddingProp="10px 20px"
                                textProp="Skip Question"
                                fontSizeProp="16px"
                                borderRadiusProp="7px"
                                borderProp="1px solid #b8b8b8"
                            />
                        </div>
                        <div className='interview_question_button_subcontainer'>
                            <div onClick={() => { setAnswer("") }}>
                                <Primary_Button
                                    bgColorProp="#F59407"
                                    fontWeightProp="600"
                                    paddingProp="10px 30px"
                                    textProp="Clear"
                                    fontSizeProp="16px"
                                    borderRadiusProp="7px"
                                />
                            </div>
                            <div onClick={submitAnswer}>
                                <Primary_Button
                                    bgColorProp="#004E89"
                                    fontWeightProp="600"
                                    paddingProp="10px 30px"
                                    textProp="Next"
                                    fontSizeProp="16px"
                                    borderRadiusProp="7px"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Interview_Questions;